import intersection from 'lodash.intersection';

export function isUserHasRole(roleMap) {
  return roleMap
    ? Boolean(roleMap.company_ids.length || roleMap.farm_ids.length)
    : false;
}

export const userRoleKeys = {
  admin: 'admin',
  caretaker: 'caregiver',
  vet: 'veterinarian',
  manager: 'manager',
  tenant_owner: 'tenant_owner',
  owner: 'primary_contact',
  super_admin: 'super_admin',
  farm_owner: 'farm_owner',
};

export const userRoleLabels = {
  admin: 'Tenant Admin',
  caregiver: 'Caregiver',
  veterinarian: 'Veterinarian',
  manager: 'Manager',
  tenant_owner: 'Tenant Owner',
  owner: 'Primary Contact',
  super_admin: 'Super Admin',
  farm_owner: 'Farm Owner',
};

export function getUserRoles(user) {
  if (!user.roles_map) return [];
  const rolesList = [];
  const {
    admin,
    caretaker,
    manager,
    owner,
    super_admin,
    tenant_owner,
    vet,
  } = user.roles_map;

  if (super_admin) rolesList.push(userRoleKeys.super_admin);
  if (admin) rolesList.push(userRoleKeys.admin);
  if (tenant_owner) rolesList.push(userRoleKeys.tenant_owner);
  if (owner && !!owner.company_ids.length) {
    rolesList.push(userRoleKeys.owner);
  }
  if (owner && !!owner.farm_ids.length) {
    rolesList.push(userRoleKeys.farm_owner);
  }
  if (isUserHasRole(manager)) rolesList.push(userRoleKeys.manager);
  if (isUserHasRole(caretaker)) rolesList.push(userRoleKeys.caretaker);
  if (isUserHasRole(vet)) rolesList.push(userRoleKeys.vet);
  return rolesList;
}

export function getUserRolesList(user) {
  return getUserRoles(user).map((key) => userRoleLabels[key]).join(', ');
}

export function isUserHasExactRoles(user, roles = []) {
  const userRoles = getUserRoles(user);
  if (userRoles.length !== roles.length) return false;
  return userRoles.every((role) => roles.includes(role));
}

export function isUserHasAnyRole(user, roles = []) {
  const userRoles = getUserRoles(user);
  return !!intersection(userRoles, roles).length;
}

// Global treatment protocols (EPN-3075): admins manage all of them,
// a vet only those applied to farms they are vet on; other roles read them.
// A company-level vet role reaches the company's internal farms only (external ones inherit no roles)
const isVetOnFarm = (vet, farm) => {
  if (!vet || !farm) return false;
  if (vet.farm_ids.includes(farm.id)) return true;
  return !farm.external && vet.company_ids.includes(farm.manager_id);
};

export const isUserCanCreateGlobalTreatmentProtocol = (user) => {
  const { admin, vet } = user?.roles_map || {};
  return !!admin || isUserHasRole(vet);
};

export const isUserCanApplyGlobalTreatmentProtocolToFarm = (user, farm) => {
  const { admin, vet } = user?.roles_map || {};
  return !!admin || isVetOnFarm(vet, farm);
};

export const isUserCanManageGlobalTreatmentProtocol = (user, farms = []) => {
  const { admin, vet } = user?.roles_map || {};
  if (admin) return true;
  return isUserHasRole(vet) && farms.every((farm) => isVetOnFarm(vet, farm));
};

export const isUserExactlyCaregiver = (user, farmId) => {
  if (!user.roles_map) return false;
  const { caretaker, manager, owner, vet, admin, super_admin, tenant_owner } = user.roles_map;
  if (admin || super_admin || tenant_owner) return false;
  const companyId = user.current_company.id;
  if (farmId) {
    const isManager = manager.farm_ids.includes(farmId) || manager.company_ids.includes(companyId);
    const isOwner = owner.farm_ids.includes(farmId) || owner.company_ids.includes(companyId);
    const isVet = vet.farm_ids.includes(farmId) || vet.company_ids.includes(companyId);
    if (isManager || isOwner || isVet) return false;
    const isCaregiver = caretaker.farm_ids.includes(farmId) || caretaker.company_ids.includes(companyId);

    return isCaregiver;
  }

  const isManager = !!manager.farm_ids.length || !!manager.company_ids.length;
  const isOwner = !!owner.farm_ids.length || !!owner.company_ids.length;
  const isVet = !!vet.farm_ids.length || !!vet.company_ids.length;
  if (isManager || isOwner || isVet) return false;
  const isCaregiver = !!caretaker.farm_ids.length || !!caretaker.company_ids.length;

  return isCaregiver;
};
